/**
 * Tiny typed i18n. Scope: marketing surfaces (landing, onboarding, privacy,
 * library, install hint, source stacker chrome). The editor UI stays French —
 * it's the working language and English speakers can still operate it.
 */

import { useCallback, useEffect, useState } from 'react';

export type Lang = 'fr' | 'en';

const STORE_KEY = 'pf-lang';

/** Phrases keyed by lookup ID. Add new keys here, both languages required. */
const STRINGS = {
  // Landing page
  'landing.title': { fr: 'PHARMAFICHE', en: 'PHARMAFICHE' },
  'landing.tagline': {
    fr: 'Génère ta fiche de révision, parfaite à chaque fois.',
    en: 'Build the perfect revision sheet, every time.',
  },
  'landing.sample_link': { fr: "Voir l'exemple →", en: 'See the sample →' },
  'landing.library_link': { fr: '📚 Mes fiches →', en: '📚 My fiches →' },
  'landing.dropzone_drop': { fr: 'Dépose ton fichier ici', en: 'Drop your file here' },
  'landing.footer': {
    fr: 'Free forever · Open source · MENA-friendly',
    en: 'Free forever · Open source · MENA-friendly',
  },

  // Source stacker
  'source.your_sources': { fr: 'Tes sources', en: 'Your sources' },
  'source.empty_prompt': {
    fr: 'Ajoute au moins une source pour commencer.',
    en: 'Add at least one source to start.',
  },
  'source.tab_pdf': { fr: 'PDF', en: 'PDF' },
  'source.tab_photo': { fr: 'Photo', en: 'Photo' },
  'source.tab_text': { fr: 'Texte', en: 'Text' },
  'source.generate': { fr: '🎨 Génère ta fiche', en: '🎨 Generate your fiche' },
  'source.none': { fr: 'Aucune source', en: 'No sources yet' },
  'source.count_one': { fr: '{n} source', en: '{n} source' },
  'source.count_other': { fr: '{n} sources', en: '{n} sources' },
  'source.chars': { fr: '{n} caractères', en: '{n} characters' },

  // Inputs — text paste
  'input.text.label_placeholder': { fr: 'Étiquette (optionnel)', en: 'Label (optional)' },
  'input.text.label_aria': { fr: 'Étiquette de la source', en: 'Source label' },
  'input.text.body_placeholder': { fr: 'Colle ton texte ici…', en: 'Paste your text here…' },
  'input.text.body_aria': { fr: 'Contenu de la source', en: 'Source content' },
  'input.text.add': { fr: 'Ajouter la source', en: 'Add source' },
  'input.text.default_label': { fr: 'Texte collé {n}', en: 'Pasted text {n}' },

  // Inputs — PDF
  'input.pdf.cta': { fr: 'Clique pour choisir un PDF', en: 'Click to pick a PDF' },
  'input.pdf.hint': {
    fr: '(ou glisse-le directement dans la fenêtre)',
    en: '(or drag it onto the window)',
  },
  'input.pdf.preview_unavailable': { fr: 'Aperçu indisponible', en: 'Preview unavailable' },
  'input.pdf.from': { fr: 'De', en: 'From' },
  'input.pdf.to': { fr: 'à', en: 'to' },
  'input.pdf.extract': { fr: 'Extraire', en: 'Extract' },
  'input.pdf.extracting': { fr: 'Extraction…', en: 'Extracting…' },
  'input.pdf.cancel': { fr: 'Annuler', en: 'Cancel' },
  'input.pdf.loading': { fr: 'Chargement du PDF…', en: 'Loading PDF…' },
  'input.pdf.err_load': {
    fr: 'Échec du chargement du PDF',
    en: 'Failed to load the PDF',
  },
  'input.pdf.err_no_text': {
    fr: 'Aucun texte extrait. Ce PDF est peut-être scanné — utilise plutôt la capture photo + OCR.',
    en: 'No text extracted. This PDF may be scanned — try the photo + OCR tab instead.',
  },
  'input.pdf.err_extract': { fr: "Échec de l'extraction", en: 'Extraction failed' },
  'input.pdf.page_one': { fr: '{n} page', en: '{n} page' },
  'input.pdf.page_other': { fr: '{n} pages', en: '{n} pages' },

  // Inputs — photo
  'input.photo.cta': {
    fr: 'Prends une photo ou choisis une image',
    en: 'Take a photo or pick an image',
  },
  'input.photo.hint': {
    fr: "(caméra de l'appareil ou fichier)",
    en: '(device camera or file)',
  },
  'input.photo.default_label': { fr: 'Photo', en: 'Photo' },
  'input.photo.capture_fallback': { fr: 'Capture', en: 'Capture' },
  'input.photo.ocr_cta': { fr: 'Reconnaître le texte (OCR)', en: 'Recognise text (OCR)' },
  'input.photo.ocr_running': { fr: 'OCR…', en: 'OCR…' },
  'input.photo.cancel': { fr: 'Annuler', en: 'Cancel' },
  'input.photo.err_no_text': {
    fr: "OCR n'a trouvé aucun texte exploitable. Essaie une photo plus nette.",
    en: 'OCR found no readable text. Try a sharper photo.',
  },
  'input.photo.err_ocr': { fr: 'Échec OCR', en: 'OCR failed' },

  // Library
  'library.sections_one': { fr: '{n} section', en: '{n} section' },
  'library.sections_other': { fr: '{n} sections', en: '{n} sections' },
  'library.tags_one': { fr: '{n} tag', en: '{n} tag' },
  'library.tags_other': { fr: '{n} tags', en: '{n} tags' },
  'library.delete_confirm': {
    fr: 'Supprimer la fiche « {title} » ? Cette action est définitive.',
    en: 'Delete fiche "{title}"? This cannot be undone.',
  },
  'library.delete_aria': { fr: 'Supprimer {title}', en: 'Delete {title}' },

  // Onboarding
  'onboarding.skip': { fr: 'Passer', en: 'Skip' },
  'onboarding.next': { fr: 'Suivant', en: 'Next' },
  'onboarding.start': { fr: 'Commencer', en: 'Get started' },
  'onboarding.step1.title': { fr: 'Ajoute tes sources', en: 'Add your sources' },
  'onboarding.step1.body': {
    fr: 'Glisse un PDF, prends en photo tes notes, ou colle ton texte. Tu peux empiler plusieurs sources avant de générer.',
    en: 'Drop a PDF, snap a photo of your notes, or paste your text. Stack multiple sources before generating.',
  },
  'onboarding.step2.title': { fr: 'Choisis ton template', en: 'Pick your template' },
  'onboarding.step2.body': {
    fr: '6 styles : Dessinée, Clinique, Infographique, Comparative, Flowchart, Minimaliste. La même fiche, présentée différemment.',
    en: '6 styles: Dessinée, Clinique, Infographique, Comparative, Flowchart, Minimaliste. Same content, different presentations.',
  },
  'onboarding.step3.title': { fr: 'Édite à ta façon', en: 'Edit your way' },
  'onboarding.step3.body': {
    fr: 'Modifie en cliquant directement, ou demande à l\'IA "ajoute la posologie pédiatrique". Tout est annulable.',
    en: 'Click to edit inline, or ask the AI "add the pediatric dose". Everything is undoable.',
  },

  // Privacy banner
  'privacy.title': {
    fr: 'Confidentialité',
    en: 'Privacy',
  },
  'privacy.body': {
    fr: "L'IA est fournie par Google Gemini. Tes données peuvent être utilisées pour améliorer le modèle. Pour un usage strictement privé, utilise le mode hors-ligne.",
    en: 'AI is provided by Google Gemini. Your data may be used to improve the model. For strictly private use, work offline.',
  },
  'privacy.dismiss': { fr: 'Compris', en: 'Got it' },

  // Library
  'library.title': { fr: '📚 Bibliothèque', en: '📚 Library' },
  'library.new': { fr: '+ Nouvelle fiche', en: '+ New fiche' },
  'library.loading': { fr: 'Chargement…', en: 'Loading…' },
  'library.empty.title': { fr: 'Aucune fiche sauvegardée', en: 'No fiches saved yet' },
  'library.empty.body': {
    fr: "Génère ta première fiche depuis la page d'accueil.",
    en: 'Generate your first fiche from the home page.',
  },
  'library.empty.cta': { fr: 'Commencer →', en: 'Get started →' },
  'library.open': { fr: '✏️ Ouvrir', en: '✏️ Open' },
  'library.opening': { fr: 'Ouverture…', en: 'Opening…' },
  'library.updated_at': { fr: 'Mise à jour', en: 'Updated' },

  // Install button
  'install.cta': { fr: '📲 Installer', en: '📲 Install' },

  // Generic
  'generic.close': { fr: 'Fermer', en: 'Close' },
  'generic.cancel': { fr: 'Annuler', en: 'Cancel' },
} as const;

type StringKey = keyof typeof STRINGS;

/** Replace `{n}` etc. tokens in a template. */
function format(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) =>
    Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : `{${k}}`,
  );
}

function detectInitialLang(): Lang {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(STORE_KEY);
    if (stored === 'fr' || stored === 'en') return stored;
  }
  if (typeof navigator !== 'undefined') {
    const nav = navigator.language?.slice(0, 2).toLowerCase();
    if (nav === 'en') return 'en';
  }
  return 'fr';
}

// Singleton lang + tiny pub/sub so all useT hooks update together
let currentLang: Lang = detectInitialLang();
const listeners = new Set<() => void>();

export function getLang(): Lang {
  return currentLang;
}

export function setLang(next: Lang): void {
  if (next === currentLang) return;
  currentLang = next;
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORE_KEY, next);
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = next === 'en' ? 'en' : 'fr';
  }
  for (const l of listeners) l();
}

export function useT(): {
  t: (key: StringKey, vars?: Record<string, string | number>) => string;
  lang: Lang;
  setLang: typeof setLang;
} {
  const [lang, setLocalLang] = useState<Lang>(currentLang);
  useEffect(() => {
    const onChange = () => setLocalLang(currentLang);
    listeners.add(onChange);
    return () => {
      listeners.delete(onChange);
    };
  }, []);
  const t = useCallback(
    (key: StringKey, vars?: Record<string, string | number>) => {
      const entry = STRINGS[key];
      if (!entry) return key;
      return format(entry[lang] ?? entry.fr, vars);
    },
    [lang],
  );
  return { t, lang, setLang };
}

/** Plain (non-hook) translation, for code outside React. */
export function tr(key: StringKey, vars?: Record<string, string | number>): string {
  const entry = STRINGS[key];
  if (!entry) return key;
  return format(entry[currentLang] ?? entry.fr, vars);
}
